import { ICircleBarChartProps } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import moment from 'moment';
import { SurveyHelper } from '../';
import { IProgressPanelProps } from '../../components/pages/survey-admin/ProgressPanel';
import ContentConstants from '../../contentConstants';
import { AudienceStatus, IAudience, SurveyStatusType } from '../../models';
import { ProgressPanelStatus } from '../../models/ProgressPanelStatus';

export const useProgressPanelState = (props: IProgressPanelProps) => {
	const { surveyName, surveyStatus, audienceId, hasBeenPublished } = props;

	const audiences: IAudience[] = ProgressPanelHelper.determineAudiences(props.audiences, audienceId);
	const openOn = ProgressPanelHelper.determineOpenOn(audiences, audienceId);
	const closeOn = audienceId ? _.find(audiences, (a) => a.audienceId === audienceId)?.closeOn : SurveyHelper.getSurveyCloseDate(audiences);
	const createdOn = SurveyHelper.getSurveyCreatedDate(props.audiences);
	const status = ProgressPanelHelper.determineStatus(audienceId, openOn, closeOn, surveyStatus, audiences, hasBeenPublished);
	const barChartProps = ProgressPanelHelper.determineBarChartProps(status, openOn, closeOn, createdOn, audienceId);

	const header = audienceId ? _.find(audiences, (a) => a.audienceId === audienceId)?.audienceName : surveyName;
	const closeDateLabel = audienceId ? ContentConstants.SURVEY_ADMIN_AUDIENCE_PROGRESS_CLOSE : ContentConstants.SURVEY_ADMIN_PROGRESS_CLOSE;
	const openDateLabel = audienceId ? ContentConstants.SURVEY_ADMIN_AUDIENCE_PROGRESS_OPEN : ContentConstants.SURVEY_ADMIN_PROGRESS_OPEN;

	return {
		barChartProps,
		closeDateLabel,
		closeOn,
		header,
		openDateLabel,
		openOn,
		status,
	};
};

export const ProgressPanelHelper = {
	determineBarChartProps: (
		status: ProgressPanelStatus,
		openOn: string | undefined,
		closeOn: string | undefined,
		createdOn: Date | undefined,
		audienceId?: number,
	): ICircleBarChartProps => {
		const barChartProps: ICircleBarChartProps = {
			size: 'md',
			unit: ContentConstants.SURVEY_ADMIN_PROGRESS_UNIT_PLURAL,
		};
		const surveyOrEventText = audienceId ? ContentConstants.EVENT : ContentConstants.SURVEY;

		switch (status) {
			case ProgressPanelStatus.Draft: {
				if (audienceId) {
					const dayProps = ProgressPanelHelper.handleDayProps(createdOn, openOn);
					return {
						...barChartProps,
						...dayProps,
						subtext: ContentConstants.SURVEY_ADMIN_PROGRESS_SUBTEXT_BEFORE_OPEN.replace('{survey_event}', surveyOrEventText),
					};
				}
				return {
					...barChartProps,
					subtext: ContentConstants.SURVEY_ADMIN_PROGRESS_SUBTEXT_NOTSCHEDULED.replace('{survey_event}', surveyOrEventText),
				};
			}
			case ProgressPanelStatus.Scheduled: {
				const dayProps = ProgressPanelHelper.handleDayProps(createdOn, openOn);
				return {
					...barChartProps,
					...dayProps,
					subtext: ContentConstants.SURVEY_ADMIN_PROGRESS_SUBTEXT_BEFORE_OPEN.replace('{survey_event}', surveyOrEventText),
				};
			}
			case ProgressPanelStatus.InProgress: {
				const dayProps = ProgressPanelHelper.handleDayProps(openOn, closeOn);
				return {
					...barChartProps,
					...dayProps,
					subtext: ContentConstants.SURVEY_ADMIN_PROGRESS_SUBTEXT_BEFORE_CLOSE.replace('{survey_event}', surveyOrEventText),
				};
			}
			case ProgressPanelStatus.Closed: {
				return {
					...barChartProps,
					subtext: ContentConstants.SURVEY_ADMIN_PROGRESS_SUBTEXT_AFTER_CLOSE.replace('{survey_event}', surveyOrEventText),
					displayValue: 0,
					value: 0,
				};
			}
			case ProgressPanelStatus.Cancelled: {
				return {
					...barChartProps,
					subtext: ContentConstants.SURVEY_ADMIN_PROGRESS_SUBTEXT_CANCELLED.replace('{survey_event}', surveyOrEventText),
				};
			}
			default: {
				return barChartProps;
			}
		}
	},
	handleDayProps: (begin?: string | Date, end?: string | Date) => {
		let daysLeft = 0;
		let totalDays = 0;
		let value = 0;
		if (moment(end).isValid() === true) {
			daysLeft = Math.ceil(moment(end).diff(new Date(), 'days', true));
			if (moment(begin).isValid() === true) {
				totalDays = Math.ceil(moment(end).diff(begin, 'days', true));
				value = (daysLeft / totalDays) * 100;
			}
		}
		const dayUnit =
			daysLeft > 1 ? ContentConstants.SURVEY_ADMIN_PROGRESS_UNIT_PLURAL : ContentConstants.SURVEY_ADMIN_PROGRESS_UNIT_SINGULAR;
		return { unit: dayUnit, value, displayValue: daysLeft };
	},
	determineStatus: (
		audienceId: number | undefined,
		openOn: string | undefined,
		closeOn: string | undefined,
		surveyStatus: SurveyStatusType | undefined,
		audiences: IAudience[],
		hasBeenPublished: boolean,
	) => {
		const status = ProgressPanelHelper.hasAudienceId(audienceId)
			? _.find(audiences, (a) => a.audienceId === audienceId)?.audienceStatus
			: surveyStatus;
		const IsCancelled = ProgressPanelHelper.hasAudienceId(audienceId)
			? status === AudienceStatus.Cancelled
			: status === SurveyStatusType.Cancelled;

		if (IsCancelled) {
			return ProgressPanelStatus.Cancelled;
		} else if (!hasBeenPublished || (ProgressPanelHelper.hasAudienceId(audienceId) && status === AudienceStatus.Incomplete)) {
			return ProgressPanelStatus.Draft;
		} else if (moment.utc().isBefore(openOn)) {
			return ProgressPanelStatus.Scheduled;
		} else if (moment.utc().isBefore(closeOn)) {
			return ProgressPanelStatus.InProgress;
		} else {
			return ProgressPanelStatus.Closed;
		}
	},
	determineAudiences: (audiences: IAudience[], audienceId?: number) => {
		if (ProgressPanelHelper.hasAudienceId(audienceId)) {
			return _.filter(audiences, (a) => a.audienceId === audienceId);
		} else {
			return audiences;
		}
	},
	determineOpenOn: (audiences: IAudience[], audienceId: number | undefined) => {
		if (ProgressPanelHelper.hasAudienceId(audienceId)) {
			const audience = _.find(audiences, (a) => a.audienceId === audienceId);
			return audience?.openOn;
		} else {
			return SurveyHelper.getSurveyOpenDate(audiences);
		}
	},
	hasAudienceId(audienceId: number | undefined) {
		return audienceId !== undefined && !isNaN(audienceId);
	},
};
